const parseInlineStyleDeclarations = (style = '') => {
	if (typeof style !== 'string' || style.trim().length === 0) return [];

	return style
		.split(';')
		.map((declaration) => declaration.trim())
		.filter(Boolean)
		.map((declaration) => {
			const separatorIndex = declaration.indexOf(':');
			if (separatorIndex < 0) return null;
			const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
			const value = declaration.slice(separatorIndex + 1).trim();
			if (!property || !value) return null;
			return [property, value];
		})
		.filter(Boolean);
};

// Merge styles by property name so overrides replace previous declarations
// rather than producing duplicated/conflicting inline rules.
export const mergeInlineStyles = (existingStyle = '', nextStyle = '') => {
	const mergedDeclarations = new Map();

	parseInlineStyleDeclarations(existingStyle).forEach(([property, value]) => {
		mergedDeclarations.set(property, value);
	});
	parseInlineStyleDeclarations(nextStyle).forEach(([property, value]) => {
		mergedDeclarations.set(property, value);
	});

	if (mergedDeclarations.size === 0) return '';
	return `${Array.from(mergedDeclarations.entries())
		.map(([property, value]) => `${property}: ${value}`)
		.join('; ')};`;
};

const applyStyleToElements = (root, selector, style) => {
	root.querySelectorAll(selector).forEach((element) => {
		const currentStyle = element.getAttribute('style') ?? '';
		element.setAttribute('style', mergeInlineStyles(currentStyle, style));
	});
};

// Legacy email template blocks include mobile-only markup (.show-on-mobile /
// .hide-on-mobile) that the old send path strips via normaliseEmailHtml.
// Unlayer injection skips that step, so remove it here before applying styles.
const stripLegacyMobileViewMarkup = (root) => {
	root.querySelectorAll('.show-on-mobile').forEach((element) => {
		element.remove();
	});

	root.querySelectorAll('.hide-on-mobile').forEach((element) => {
		element.classList.remove('hide-on-mobile');
		if (!element.classList.length) {
			element.removeAttribute('class');
		}
	});
};

const applyTwoColumnRowStyles = (root) => {
	root.querySelectorAll('tr').forEach((row) => {
		const cells = Array.from(row.children).filter((cell) =>
			['TH', 'TD'].includes(cell.tagName)
		);
		const [firstCell, secondCell] = cells;
		if (!firstCell || !secondCell) return;

		const isHeaderRow = cells.every((cell) => cell.tagName === 'TH');
		const firstCellStyle = isHeaderRow
			? 'padding: 8px 12px 10px 0; width: 32%; text-align: left; font-weight: 600; vertical-align: top; line-height: 17px; mso-line-height-rule: exactly'
			: 'padding: 7px 12px 7px 0; text-align: left; vertical-align: top; line-height: 17px; mso-line-height-rule: exactly; word-break: break-word';
		const secondCellStyle = isHeaderRow
			? 'padding: 8px 0 10px 12px; width: 68%; text-align: left; font-weight: 600; vertical-align: top; line-height: 17px; mso-line-height-rule: exactly'
			: 'padding: 7px 0 7px 12px; text-align: left; vertical-align: top; line-height: 17px; mso-line-height-rule: exactly; word-break: break-word';

		firstCell.setAttribute(
			'style',
			mergeInlineStyles(firstCell.getAttribute('style') ?? '', firstCellStyle)
		);
		secondCell.setAttribute(
			'style',
			mergeInlineStyles(secondCell.getAttribute('style') ?? '', secondCellStyle)
		);
	});
};

export const applyLegacyScopeTableStyles = (html) => {
	if (typeof html !== 'string' || html.trim().length === 0) return html;

	try {
		const parser = new DOMParser();
		const document = parser.parseFromString(`<div id="soa-root">${html}</div>`, 'text/html');
		const root = document.querySelector('#soa-root');
		if (!root) return html;

		stripLegacyMobileViewMarkup(root);

		const baseTextStyle = 'color: rgb(51, 51, 51); font-family: Arial, sans-serif; font-size: 11px';
		const headerTextStyle = `${baseTextStyle}; font-weight: 600`;

		applyStyleToElements(root, '.table-container', 'width: 100%');

		applyStyleToElements(
			root,
			'table',
			'width: 100%; max-width: 100%; border-collapse: collapse; table-layout: fixed; mso-table-lspace: 0pt; mso-table-rspace: 0pt'
		);
		applyStyleToElements(root, 'tr', baseTextStyle);
		applyStyleToElements(
			root,
			'th',
			`${headerTextStyle}; text-align: left; vertical-align: top`
		);
		applyStyleToElements(
			root,
			'td',
			`${baseTextStyle}; text-align: left; vertical-align: top`
		);
		applyTwoColumnRowStyles(root);
		applyStyleToElements(root, 'p', `${baseTextStyle}; line-height: 20px`);
		applyStyleToElements(root, 'th p, th span', headerTextStyle);
		applyStyleToElements(root, 'td p, td span', baseTextStyle);

		return root.innerHTML;
	} catch {
		return html;
	}
};

/**
 * Given html as a string, will try to:
 * - parse it and find all paragraphs
 * - apply inline styles to those paragraphs to match the legacy scope of advice email formatting
 * 
 * If any step of this process fails, it will return the original HTML string unmodified
 * 
 * This is used to apply consistent formatting to the additional paragraphs injected into the email template alongside the scope of advice table, without needing to modify the template itself
 */
export const applyParagraphStylesToHtml = (html) => {
	if (typeof html !== 'string' || html.trim().length === 0) return html;

	try {
		const parser = new DOMParser();
		const document = parser.parseFromString(`<div id="soa-copy-root">${html}</div>`, 'text/html');
		const root = document.querySelector('#soa-copy-root');
		if (!root) return html;

		const paragraphStyle =
			'color: rgb(51, 51, 51); font-family: Arial, sans-serif; font-size: 11px; line-height: 17px; mso-line-height-rule: exactly; margin: 0';
		const spacedParagraphStyle = `${paragraphStyle}; margin-top: 17px`;

		applyStyleToElements(root, 'div', 'width: 100%');
		root.querySelectorAll('p').forEach((paragraph, index) => {
			const currentStyle = paragraph.getAttribute('style') ?? '';
			paragraph.setAttribute(
				'style',
				mergeInlineStyles(currentStyle, index === 0 ? paragraphStyle : spacedParagraphStyle)
			);
		});

		return root.innerHTML;
	} catch {
		return html;
	}
};
